import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import {
  useLazyGetCodTransferListQuery,
  useLazyListShipmentsByCodTransferIdPrintQuery,
} from "store/services/PaymentsApi/codTransferApi";
import { useLazyListVendorPendingPaymentsPrintForVendorQuery } from "store/services/reportApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { handlePrint } from "utils/printCodTransfer";
import { IconButton } from "@mui/material";

const CodTransferListScreen = () => {
  const { isVendor, loggedInUser } = useAuth();
  const navigate = useNavigate();
  const vendorId = loggedInUser?.typeRef;
  const [fetchVendorPending, { data: vendorPending }] =
    useLazyListVendorPendingPaymentsPrintForVendorQuery();

  React.useEffect(() => {
    if (isVendor && vendorId) fetchVendorPending({ vendorId });
  }, [fetchVendorPending, isVendor, vendorId]);
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const [fetchShipments] = useLazyListShipmentsByCodTransferIdPrintQuery();

  const breadcrumbs = [
    {
      title: "COD Transfer",
    },
  ];

  const CodTransferListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "COD Transfer ID",
      selector: (row) => row?.codTransferId,
      cell: (row) => {
        const displayId = row?.codTransferId;
        const path = !isVendor
          ? `/app/crm/cod-transfer/${row?._id}/detail`
          : `/app/vendor/cod-transfer/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
      minWidth: "200px",
    },
    {
      name: "Vendor Profile",
      selector: (row) => row?.vendorOwnerName,
      cell: (row) => (
        <div title={row?.vendorOwnerName} className="line-clamp-2">
          {row?.vendorOwnerName ? (
            <HighlightedText
              text={row?.vendorOwnerName}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Transfer Amount",
      selector: (row) => row?.transferAmount,
      cell: (row) => (
        <div title={row?.transferAmount} className="line-clamp-2">
          {row?.transferAmount !== null
            ? `${row?.transferAmount?.toFixed(2)}`
            : "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Transfer Mode",
      selector: (row) => row?.transferMode,
      cell: (row) => (
        <div title={row?.transferMode} className="line-clamp-2">
          {row?.transferMode || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver,
      cell: (row) => (
        <div title={row?.receiver} className="line-clamp-2">
          {row?.receiver ? (
            <HighlightedText text={row?.receiver} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              isVendor
                ? `/app/vendor/cod-transfer/${row._id}/detail`
                : `/app/crm/cod-transfer/${row._id}/detail`
            }
          >
            <IconButton size="small" aria-label="View COD Transfer">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
          <IconButton
            size="small"
            aria-label="Print COD Transfer"
            onClick={() => handlePrint(row._id, fetchShipments)}
          >
            <PrintOutlined color="primary" />
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchCodTransfer,
    {
      data: CodTransferList,
      isLoading: loadingCodTransfer,
      isFetching: fetchingCodTransfer,
      error,
    },
  ] = useLazyGetCodTransferListQuery();

  React.useEffect(() => {
    fetchCodTransfer({
      active: activeTab === "Active" ? "true" : "false",
      ...(keyword && { keyword }),
      page: currentPage,
      limit: perPage,
    });
  }, [activeTab, fetchCodTransfer, keyword, currentPage, perPage]);

  useAppMeta(`${activeTab} COD Transfer`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        {!isVendor && (
          <CustomTabs tabs={CodTransferListTabs} activeTab={activeTab} />
        )}
        <TableFilter searchLabel="COD Transfer ID">
          <div className="font-medium"></div>
        </TableFilter>

        {isVendor && (
          <Box display="flex" justifyContent="flex-end" m={2}>
            <Button
              variant="contained"
              startIcon={<PrintOutlined />}
              onClick={() => navigate("/app/vendor/pending-cod")}
              color="primary"
              size="medium"
            >
              Pending COD RS. {vendorPending?.totalCODAmount?.toFixed(2) || 0}
            </Button>
          </Box>
        )}

        <AppTable
          columns={columns}
          data={CodTransferList?.data}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loadingCodTransfer || fetchingCodTransfer}
          paginationTotalRows={CodTransferList?.totalItems ?? 0}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default CodTransferListScreen;

const PageActions = () => {
  const { isVendor } = useAuth();
  return (
    <div
      style={{
        display: "flex",
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      {!isVendor && (
        <Link to="/app/crm/cod-transfer/create">
          <Button startIcon={<AddIcon />} variant="contained">
            New COD Transfer
          </Button>
        </Link>
      )}
    </div>
  );
};
